import { useEffect, useRef, useState } from "react";
import assetClient from "src/services/assetClient";

/**
 * Polls `/api/v1.0/asset/video/{ref}/encoding-progress` while the
 * caller indicates the video is still being processed by Bitmovin.
 *
 * Use alongside `useAzureVideoDownload`: that hook tells you when the
 * m3u8 manifest is in blob storage (i.e. encoding is FINISHED). This
 * one tells you what percentage of the way through Bitmovin is right
 * now. Together they let the UI render a real progress bar instead of
 * a generic spinner during the 2–10 minute encoding window.
 *
 * Polls every 4 seconds while `enabled` is true. Stops automatically
 * when status is "finished" / "error" / "canceled", on unmount, or
 * after `MAX_CONSECUTIVE_FAILURES` consecutive errors (so a persistent
 * 500 doesn't turn into a silent infinite poll).
 *
 * After polling stops on a terminal status, an internal heartbeat
 * keeps `elapsedSeconds` ticking so the UI doesn't appear frozen
 * during the brief gap between Bitmovin's "finished" signal and the
 * m3u8 manifest landing in blob storage.
 */

const POLL_INTERVAL_MS = 4_000;
const ELAPSED_TICK_MS = 1_000;
const MAX_CONSECUTIVE_FAILURES = 5;

export interface VideoEncodingProgress {
	status:
		| "queued"
		| "running"
		| "finished"
		| "error"
		| "canceled"
		| "created"
		| "unknown";
	progress: number | null;
}

export interface VideoEncodingProgressState {
	progress: VideoEncodingProgress | null;
	elapsedSeconds: number;
}

export function useVideoEncodingProgress(
	reference: string | null | undefined,
	enabled: boolean
): VideoEncodingProgressState {
	const [state, setState] = useState<VideoEncodingProgressState>({
		progress: null,
		elapsedSeconds: 0
	});
	const pollTimerRef = useRef<number | null>(null);
	const elapsedTimerRef = useRef<number | null>(null);
	const startedAtRef = useRef<number | null>(null);
	const consecutiveFailuresRef = useRef<number>(0);

	useEffect(() => {
		const clearTimers = () => {
			if (pollTimerRef.current !== null) {
				window.clearTimeout(pollTimerRef.current);
				pollTimerRef.current = null;
			}
			if (elapsedTimerRef.current !== null) {
				window.clearInterval(elapsedTimerRef.current);
				elapsedTimerRef.current = null;
			}
		};

		if (!enabled || !reference) {
			clearTimers();
			startedAtRef.current = null;
			consecutiveFailuresRef.current = 0;
			setState({ progress: null, elapsedSeconds: 0 });
			return;
		}

		let cancelled = false;
		startedAtRef.current = Date.now();
		consecutiveFailuresRef.current = 0;

		// Independent 1-Hz tick that keeps the elapsed counter moving
		// even after polling stops on a terminal status. Without this,
		// the UI appears frozen during the brief gap between Bitmovin's
		// "finished" signal and the m3u8 landing in blob storage.
		elapsedTimerRef.current = window.setInterval(() => {
			if (cancelled || !startedAtRef.current) return;
			const elapsed = Math.round((Date.now() - startedAtRef.current) / 1000);
			setState((prev) =>
				prev.elapsedSeconds === elapsed
					? prev
					: { ...prev, elapsedSeconds: elapsed }
			);
		}, ELAPSED_TICK_MS);

		const poll = async () => {
			if (cancelled) return;
			try {
				const resp = await assetClient.get<VideoEncodingProgress | null>(
					`/api/v1.0/asset/video/${reference}/encoding-progress`
				);
				if (cancelled) return;
				consecutiveFailuresRef.current = 0;
				setState((prev) => ({
					progress: resp.data ?? null,
					elapsedSeconds: prev.elapsedSeconds
				}));
				const status = resp.data?.status;
				if (
					status === "finished" ||
					status === "error" ||
					status === "canceled"
				) {
					// Stop polling but leave the elapsed-tick interval
					// running so the UI keeps showing a moving timer.
					if (pollTimerRef.current !== null) {
						window.clearTimeout(pollTimerRef.current);
						pollTimerRef.current = null;
					}
					return;
				}
			} catch (err) {
				// Transient errors are expected (the endpoint can 404 if
				// the encoding-id cache hasn't landed yet, or 500 during
				// a deploy). Keep trying for a bounded window so the
				// existing playback poll has time to surface success
				// regardless. After MAX_CONSECUTIVE_FAILURES in a row we
				// stop polling entirely — the UI gracefully degrades to
				// the indeterminate "Preparing video for playback" state
				// rather than hammering the backend forever.
				if (cancelled) return;
				consecutiveFailuresRef.current += 1;
				if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
					// eslint-disable-next-line no-console
					console.warn(
						"useVideoEncodingProgress: stopping after",
						MAX_CONSECUTIVE_FAILURES,
						"consecutive failures",
						err
					);
					if (pollTimerRef.current !== null) {
						window.clearTimeout(pollTimerRef.current);
						pollTimerRef.current = null;
					}
					return;
				}
			}
			if (!cancelled) {
				pollTimerRef.current = window.setTimeout(poll, POLL_INTERVAL_MS);
			}
		};

		poll();

		return () => {
			cancelled = true;
			clearTimers();
		};
	}, [reference, enabled]);

	return state;
}
